/**
 * Primitivas do design system SGA Petro.
 *
 * Ao integrar no codebase do **SGA Petro Frontend**, troque estes componentes
 * pelos `Sga*` que já existem lá (`SgaTable`, `SgaModal`, `SgaInputs`,
 * `SgaSegmented`) — as props aqui foram nomeadas para essa substituição ser
 * mecânica. As classes CSS vêm de `styles/kit.css`, que é o `_kit.css` do
 * design system sem alteração.
 */
import { Icon } from '@iconify/react';
import type { CSSProperties, ReactNode } from 'react';

import type { Kind } from '../lib/types';

/** Cor de preenchimento: ponto, barra, sparkline. Requisito é 3:1 (não texto),
 *  e a cor de marca passa. Texto usa as variantes `-texto`. */
const COR: Record<Kind, string> = {
  ok: 'var(--sga-tag-text)',
  warn: 'var(--sga-warning)',
  err: 'var(--sga-error)',
  mut: 'var(--sga-gray-5)',
};

/** Ponto de status de 9px. Pulsa em `warn` — sincronizando/throttling. */
export function Ponto({ kind, pulsa }: { kind: Kind; pulsa?: boolean }) {
  const cor = COR[kind];
  return (
    <span
      className={pulsa || kind === 'warn' ? 'sga-dot sga-dot--pulsa' : 'sga-dot'}
      style={{ background: cor, boxShadow: `0 0 0 3px ${cor}22` }}
    />
  );
}

export function Tag({
  kind = 'ok',
  children,
  grande,
  solido,
}: {
  kind?: Kind;
  children: ReactNode;
  grande?: boolean;
  /**
   * Fundo sólido em vez de tingido. Necessário quando a tag fica dentro de um
   * container que já é tingido (faixa de atenção): a tinta da tag somada à do
   * container derrubava o contraste do texto para 4.34:1, abaixo de AA.
   */
  solido?: boolean;
}) {
  // `ok` usa a tag padrão do kit; os outros estados usam tintas derivadas.
  //
  // O texto vai nas variantes `-texto` (mesmo hue, mais escuro): a cor original
  // em 12px dá 2.48:1 no laranja e 3.27:1 no vermelho, abaixo do mínimo de
  // 4.5:1 do WCAG AA. O fundo tingido continua com a cor de marca.
  const estilos: Record<Kind, CSSProperties> = {
    ok: {},
    warn: { background: 'rgba(230,145,56,.16)', color: 'var(--sga-warning-texto)' },
    err: { background: 'rgba(255,77,79,.14)', color: 'var(--sga-error-texto)' },
    mut: { background: 'var(--sga-gray-2)', color: 'var(--sga-fg-2)' },
  };
  const base = estilos[kind];
  return (
    <span
      className="tag"
      style={{
        ...base,
        ...(solido
          ? { background: 'var(--sga-bg-container)', border: '1px solid var(--sga-border-gray)' }
          : {}),
        ...(grande ? { padding: '5px 12px', fontSize: 12.5 } : {}),
      }}
    >
      {children}
    </span>
  );
}

export function TagComPonto({
  kind,
  children,
  grande,
}: {
  kind: Kind;
  children: ReactNode;
  grande?: boolean;
}) {
  return (
    <Tag kind={kind} grande={grande}>
      <Ponto kind={kind} />
      {children}
    </Tag>
  );
}

export function Botao({
  variante = 'default',
  icone,
  children,
  ...props
}: {
  variante?: 'primary' | 'default' | 'warning' | 'danger' | 'link';
  icone?: string;
  children?: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={`btn btn-${variante}`} {...props}>
      {icone && <Icon icon={icone} width={16} />}
      {children}
    </button>
  );
}

export function Segmented<T extends string>({
  valor,
  opcoes,
  onChange,
  largo,
}: {
  valor: T;
  opcoes: { valor: T; rotulo: string }[];
  onChange: (v: T) => void;
  largo?: boolean;
}) {
  return (
    <div className="seg" style={largo ? { display: 'flex', width: '100%' } : undefined}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          aria-pressed={valor === o.valor}
          onClick={() => onChange(o.valor)}
          style={largo ? { flex: 1 } : undefined}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/** Barra de proporção de 5–6px usada na coluna "Fila" e nos medidores. */
export function Barra({
  pct,
  kind = 'ok',
  altura = 5,
}: {
  pct: number;
  kind?: Kind;
  altura?: number;
}) {
  return (
    <div className="sga-barra" style={{ height: altura }}>
      <div
        className="sga-barra__preenchida"
        style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: COR[kind] }}
      />
    </div>
  );
}

/**
 * Sparkline de 16 barras. `serie` vem do gateway já em percentual do pico,
 * então a altura é o próprio valor.
 */
export function Sparkline({
  serie,
  altura = 44,
  kind = 'ok',
}: {
  serie: number[];
  altura?: number;
  kind?: Kind;
}) {
  const pontos = serie.length ? serie : Array.from({ length: 16 }, () => 0);
  return (
    <div className="sga-spark" style={{ height: altura }}>
      {pontos.map((v, i) => (
        <span
          key={i}
          style={{
            height: `${Math.max(4, v)}%`,
            background: COR[kind],
            // Barras mais recentes ficam mais opacas: dá direção ao gráfico.
            opacity: 0.35 + (v / 100) * 0.6,
          }}
        />
      ))}
    </div>
  );
}

export function Card({
  children,
  titulo,
  acoes,
  style,
  className,
}: {
  children: ReactNode;
  titulo?: ReactNode;
  acoes?: ReactNode;
  style?: CSSProperties;
  /** Extra para telas que precisam controlar o layout do card (ex.: cabeçalho
   *  fixo com corpo rolável). */
  className?: string;
}) {
  return (
    <section className={className ? `sga-card ${className}` : 'sga-card'} style={style}>
      {(titulo || acoes) && (
        <header className="sga-card__head">
          <h2 className="sga-card__titulo">{titulo}</h2>
          <div className="sga-card__acoes">{acoes}</div>
        </header>
      )}
      {children}
    </section>
  );
}

export function Kpi({
  icone,
  rotulo,
  valor,
  nota,
  cor,
}: {
  icone: string;
  rotulo: string;
  valor: ReactNode;
  nota?: ReactNode;
  cor?: string;
}) {
  return (
    <div className="sga-kpi">
      <span className="sga-kpi__rotulo">
        <Icon icon={icone} width={14} />
        {rotulo}
      </span>
      <strong className="sga-kpi__valor" style={cor ? { color: cor } : undefined}>
        {valor}
      </strong>
      {nota && <span className="sga-kpi__nota">{nota}</span>}
    </div>
  );
}

export function Campo({
  rotulo,
  obrigatorio,
  hint,
  children,
  flex,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  hint?: string;
  children: ReactNode;
  flex?: number;
}) {
  return (
    <label className="field" style={flex ? { flex } : undefined}>
      <span className="field-label">
        {rotulo}
        {obrigatorio && <span className="req">*</span>}
      </span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export function TituloTela({
  icone,
  titulo,
  contexto,
  acoes,
}: {
  icone: string;
  titulo: string;
  contexto?: string;
  acoes?: ReactNode;
}) {
  return (
    <div className="sga-tela__head">
      <Icon icon={icone} width={28} style={{ color: 'var(--sga-primary-text)' }} />
      <h1 className="sga-tela__titulo">{titulo}</h1>
      {contexto && <span className="sga-tela__contexto">{contexto}</span>}
      <span style={{ flex: 1 }} />
      {acoes}
    </div>
  );
}

/** Estado vazio: nunca deixar uma tabela em branco sem explicação. */
export function Vazio({ icone, texto }: { icone: string; texto: string }) {
  return (
    <div className="sga-vazio">
      <Icon icon={icone} width={26} />
      <span>{texto}</span>
    </div>
  );
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <div className="sga-vazio">
      <Icon icon="mdi:sync" width={22} className="sga-girando" />
      <span>{texto}</span>
    </div>
  );
}

export function ErroInline({ mensagem, aoTentar }: { mensagem: string; aoTentar?: () => void }) {
  return (
    <div className="sga-vazio sga-vazio--erro">
      <Icon icon="mdi:alert-circle-outline" width={22} />
      <span>{mensagem}</span>
      {aoTentar && (
        <Botao variante="link" onClick={aoTentar}>
          Tentar de novo
        </Botao>
      )}
    </div>
  );
}
